import { ArrowBack, MenuBook, Sync } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChapterSaveProgress, ChapterSaveProgressDialog } from './chapter-save-progress-dialog';
import { ChapterSceneVariantOption, ChapterVariantsDrawer } from './chapter-variants-drawer';
import { PublishEditorHeader } from './publish-editor-header';

import { OutlineDrawer } from '@/components/editor/OutlineDrawer';
import { RichTextEditor } from '@/components/editor/rich-text-editor';
import { UnreadyScenesDialog } from '@/features/chapters/components/UnreadyScenesDialog';
import { useCreateChapter } from '@/features/chapters/hooks/use-mutation-hooks';
import { usePublishChapter } from '@/features/chapters/hooks/use-publish-chapter';
import { ensureStructuredChapterHtml, parseChapterSections, renderChapterSections, replaceSceneSections, sceneSectionMap } from '@/features/chapters/lib/chapter-sections';
import { stripCommentAnchors } from '@/features/chapters/lib/strip-comment-anchors';
import { useWriteScene } from '@/features/scenes/hooks/use-mutation-hooks';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { calculateWordCount } from '@/lib/text-service';
import { ContentDocument, NarrativeSummary } from '@/lib/types';

interface PublishEditorContentProps {
  chapterId: string;
}

export function PublishEditorContent({ chapterId }: PublishEditorContentProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user, backend } = useFirebase();
  const { userData } = useUserProfile();
  const navigate = useNavigate();
  const { novel, batchSaveItems, novelDao, saveItem, updateNarrativeSummary, updateNarrativeItem, getNewId } = useNovelRepository();
  const { assembleFinalEdition: assembleChapterFinalEdition, updatePublishStatus, isAssembling, isUpdatingStatus, unreadyData, setUnreadyData } = usePublishChapter();
  const { createChapter } = useCreateChapter();
  const { createFullScene } = useWriteScene();
  const { hasFeature } = useSubscription();
  const canUseAutosave = hasFeature('autosave');

  const [editorContent, setEditorContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [saveProgress, setSaveProgress] = useState<ChapterSaveProgress | null>(null);
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [selectedContentDoc, setSelectedContentDoc] = useState<ContentDocument | null>(null);
  const [sceneContentDocs, setSceneContentDocs] = useState<Record<string, ContentDocument>>({});
  const [isOutlineOpen, setIsOutlineOpen] = useState(true);
  const [expandedChapters, setExpandedChapters] = useState<Record<string, boolean>>({ [chapterId]: true });
  const loadedContentId = useRef<string | null>(null);

  const chapter = useMemo(() => {
    return novel?.summaries?.chapters?.[chapterId] as NarrativeSummary | undefined;
  }, [novel, chapterId]);

  const mainContentId = chapter?.mainContentId;
  const translatedVariantLabel = (kind: 'default' | 'dedicated' | 'unnamed') => {
    const language = i18n.resolvedLanguage || i18n.language;
    const labels = {
      en: { default: 'Default', dedicated: 'Dedicated', unnamed: 'Unnamed' },
      es: { default: 'Predeterminada', dedicated: 'Dedicada', unnamed: 'Sin nombre' },
      ca: { default: 'Predeterminada', dedicated: 'Dedicada', unnamed: 'Sense nom' },
    };
    return (labels[language as keyof typeof labels] || labels.en)[kind];
  };
  const scenes = useMemo(() => (Object.values(novel?.summaries?.scenes || {}) as NarrativeSummary[])
    .filter((scene) => scene.chapterId === chapterId && !scene.isArchive)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [novel?.summaries?.scenes, chapterId]);
  const outlineChapters = useMemo(() => Object.values(novel?.summaries?.chapters || {}) as NarrativeSummary[], [novel?.summaries?.chapters]);
  const outlineScenes = useMemo(() => Object.values(novel?.summaries?.scenes || {}) as NarrativeSummary[], [novel?.summaries?.scenes]);
  const effectiveVariantSceneRefs = useMemo(() => {
    const variantIds = [chapter?.mainContentId, ...(chapter?.contentVariants || [])].filter((id): id is string => Boolean(id));
    return Object.fromEntries(variantIds.map((variantId) => [variantId, chapter?.variantSceneRefs?.[variantId] || Object.fromEntries(
      scenes.filter((scene) => scene.mainContentId).map((scene) => [scene.id, {
        contentId: scene.mainContentId!,
        version: scene.contentVariantVersions?.[scene.mainContentId!] ?? sceneContentDocs[scene.mainContentId!]?.scratchpadVersion ?? 0,
      }])
    )]));
  }, [chapter?.contentVariants, chapter?.mainContentId, chapter?.variantSceneRefs, sceneContentDocs, scenes]);
  const selectedSceneRefs = selectedContentId ? effectiveVariantSceneRefs[selectedContentId] : undefined;

  useEffect(() => {
    if (mainContentId && !selectedContentId) setSelectedContentId(mainContentId);
  }, [mainContentId, selectedContentId]);

  // 1. Fetch content document
  useEffect(() => {
    let active = true;
    const fetchContent = async () => {
      if (!novel.id || !selectedContentId || !backend) {
        setIsLoading(false);
        return;
      }

      if (loadedContentId.current !== selectedContentId) setIsLoading(true);
      try {
        const doc = await backend.content.getContent(novel.ownerId, novel, selectedContentId);
        if (doc && active) {
          let structuredContent = doc.scratchpad || '';
          if (parseChapterSections(structuredContent).length === 0) {
            const storedRefs = chapter?.variantSceneRefs?.[selectedContentId] || {};
            const rebuiltRefs: Record<string, { contentId: string; version: number }> = {};
            const rebuiltSections = await Promise.all(scenes.map(async (scene) => {
              const contentId = storedRefs[scene.id]?.contentId || scene.mainContentId;
              if (!contentId) throw new Error(`Scene ${scene.id} has no content variant to assemble`);
              const sceneDoc = await backend.content.getContent(novel.ownerId, novel, contentId);
              if (!sceneDoc) throw new Error(`Scene content ${contentId} could not be loaded`);
              rebuiltRefs[scene.id] = { contentId, version: sceneDoc.scratchpadVersion };
              return { type: 'scene' as const, id: scene.id, sceneId: scene.id, html: sceneDoc.scratchpad || '<p></p>' };
            }));
            structuredContent = renderChapterSections(rebuiltSections);
            await backend.content.updateContent(novel.ownerId, novel, selectedContentId, {
              scratchpad: structuredContent,
              wordCount: calculateWordCount(structuredContent),
            });
            if (chapter) {
              const updatedChapter = {
                ...chapter,
                variantSceneRefs: { ...(chapter.variantSceneRefs || {}), [selectedContentId]: rebuiltRefs },
              };
              await saveItem('narrative', 'chapters', updatedChapter);
              await updateNarrativeSummary('chapters', chapterId, updatedChapter);
            }
            doc.scratchpad = structuredContent;
          } else {
            structuredContent = ensureStructuredChapterHtml(structuredContent, scenes.map((scene) => scene.id));
          }
          setEditorContent(structuredContent);
          if (structuredContent !== doc.scratchpad) {
            await backend.content.updateContent(novel.ownerId, novel, selectedContentId, { scratchpad: structuredContent });
            doc.scratchpad = structuredContent;
          }
          setSelectedContentDoc(doc);
          loadedContentId.current = selectedContentId;
        }
      } catch (err) {
        console.error('Failed to load compiled chapter content:', err);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchContent();
    return () => {
      active = false;
    };
  }, [novel, selectedContentId, backend, scenes]);

  useEffect(() => {
    if (!novel.id) return;
    const ids = Array.from(new Set(scenes.flatMap((scene) => [scene.mainContentId, ...(scene.contentVariants || [])]).filter((id): id is string => Boolean(id))));
    let active = true;
    void Promise.all(ids.map(async (id) => [id, await backend.content.getContent(novel.ownerId, novel, id)] as const)).then((entries) => {
      if (!active) return;
      setSceneContentDocs(Object.fromEntries(entries.filter((entry): entry is [string, ContentDocument] => Boolean(entry[1]))));
    });
    return () => { active = false; };
  }, [backend, novel, scenes]);

  // 2. Drift Detection
  const hasDrift = useMemo(() => {
    if (!chapter || !selectedContentDoc) return false;
    return scenes.some((scene) => {
      const ref = selectedSceneRefs?.[scene.id];
      if (ref) return sceneContentDocs[ref.contentId]?.scratchpadVersion !== ref.version;
      const lastCompiledAt = chapter.compiledSceneVersions?.[scene.id];
      return !lastCompiledAt || scene.updatedAt > lastCompiledAt;
    });
  }, [chapter, sceneContentDocs, scenes, selectedContentDoc, selectedSceneRefs]);

  const handleEditorChange = (newContent: string) => {
    setEditorContent(newContent);
    setIsDirty(true);
  };

  const handleReorderScenes = async (scenesByChapter: Record<string, NarrativeSummary[]>) => {
    const now = new Date().toISOString();
    const updates = Object.entries(scenesByChapter).flatMap(([targetChapterId, chapterScenes]) => (
      chapterScenes.map((scene, order) => ({
        ...scene,
        chapterId: targetChapterId === 'unsorted' ? '' : targetChapterId,
        order,
        updatedAt: now,
      }))
    ));

    await batchSaveItems('narrative', 'scenes', updates);
    await novelDao.batchUpdateSummaries(
      novel.ownerId,
      novel,
      'narrative',
      'scenes',
      Object.fromEntries(updates.map((scene) => [scene.id, scene])),
    );
  };

  const handleCommentChange = async (newContent: string) => {
    if (!novel.id || !selectedContentId) return;
    // Comment anchors are annotation metadata: persist them without creating a
    // prose version or synchronizing the chapter back into its scenes.
    setEditorContent(newContent);
    await backend.content.updateContent(novel.ownerId, novel, selectedContentId, {
      scratchpad: newContent,
    });
  };

  const handleSave = async (contentOverride?: string, silent = false) => {
    if (!novel.id || !selectedContentId || !backend || !chapter || isSaving) return false;

    setIsSaving(true);
    try {
      const contentToSave = contentOverride ?? editorContent;
      const wordCount = calculateWordCount(contentToSave);
      const totalSteps = scenes.length + 1;
      let completed = 0;
      const report = (label: string) => {
        if (!silent) setSaveProgress({ completed, total: totalSteps, label });
      };

      report(t('chapters.publish.progress_chapter'));
      const sceneParts = sceneSectionMap(contentToSave);
      if (scenes.every((scene) => sceneParts[scene.id] !== undefined)) {
        const refs: Record<string, { contentId: string; version: number }> = { ...(selectedSceneRefs || {}) };
        for (let i = 0; i < scenes.length; i++) {
          const scene = scenes[i];
          report(t('chapters.publish.progress_scene', { current: i + 1, total: scenes.length, name: scene.name }));
          const contentId = refs[scene.id]?.contentId || scene.mainContentId;
          if (contentId) {
            const sceneDoc = sceneContentDocs[contentId] || await backend.content.getContent(novel.ownerId, novel, contentId);
            if (sceneDoc) {
              const sceneProse = stripCommentAnchors(sceneParts[scene.id]);
              const didSave = stripCommentAnchors(sceneDoc.scratchpad) !== sceneProse
                ? await backend.content.saveContentVersion(novel.ownerId, novel, contentId, sceneProse, false, sceneDoc.scratchpadVersion)
                : false;
              const version = sceneDoc.scratchpadVersion + (didSave ? 1 : 0);
              refs[scene.id] = { contentId, version };
              if (didSave) {
                setSceneContentDocs((current) => ({
                  ...current,
                  [contentId]: { ...sceneDoc, scratchpad: sceneProse, scratchpadVersion: version },
                }));
              }
              const contentVariantVersions = { ...(scene.contentVariantVersions || {}), [contentId]: version };
              await updateNarrativeItem('scenes', scene.id, { contentVariantVersions });
            }
          }
          completed += 1;
        }
        await backend.content.updateContent(novel.ownerId, novel, selectedContentId, {
          scratchpad: contentToSave,
          wordCount,
        });
        const updatedChapter = {
          ...chapter,
          variantSceneRefs: { ...(chapter.variantSceneRefs || {}), [selectedContentId]: refs },
        };
        await saveItem('narrative', 'chapters', updatedChapter);
        await updateNarrativeSummary('chapters', chapterId, updatedChapter);
        setSelectedContentDoc((current) => current ? { ...current, scratchpad: contentToSave, wordCount } : current);
      } else {
        if (!silent) {
          toast({
            title: t('common.errors.title'),
            description: t('chapters.publish.variant_scene_count_mismatch'),
            variant: 'destructive',
          });
        }
        return false;
      }
      completed += 1;

      setIsDirty(false);
      if (!silent) {
        setSaveProgress({ completed: totalSteps, total: totalSteps, label: t('chapters.publish.saving_complete') });
        toast({
          title: t('common.success'),
          description: t('chapters.publish.saved_success')
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to save final edition changes:', err);
      if (!silent) {
        toast({
          title: t('common.errors.title'),
          description: t('chapters.publish.save_failed'),
          variant: 'destructive'
        });
      }
      return false;
    } finally {
      setIsSaving(false);
      if (!silent) window.setTimeout(() => setSaveProgress(null), 350);
    }
  };

  // Auto-save debounced
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (canUseAutosave && autosaveEnabled && isDirty && editorContent) {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => {
        void handleSave(undefined, true);
      }, 30000); // 30 seconds auto-save
    }
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [isDirty, editorContent, canUseAutosave, autosaveEnabled]);

  // Calculate words/reading time
  const wordCount = useMemo(() => calculateWordCount(editorContent), [editorContent]);
  const readingTime = useMemo(() => Math.ceil(wordCount / 200), [wordCount]);

  const variantOptions = useMemo(() => {
    if (!chapter?.mainContentId) return [];
    return [chapter.mainContentId, ...(chapter.contentVariants || [])].map((id) => ({
      id,
      name: novel?.summaries?.contents?.[id]?.name || chapter.name,
      notes: novel?.summaries?.contents?.[id]?.notes,
      updatedAt: novel?.summaries?.contents?.[id]?.updatedAt,
      isMain: id === chapter.mainContentId,
    }));
  }, [chapter, novel?.summaries?.contents]);

  const chapterSceneVariants = useMemo((): ChapterSceneVariantOption[] => scenes.map((scene) => {
    const ids = [scene.mainContentId, ...(scene.contentVariants || [])].filter((id): id is string => Boolean(id));
    const ref = selectedSceneRefs?.[scene.id];
    const selectedSceneContentId = ref?.contentId || scene.mainContentId || ids[0] || '';
    return {
      sceneId: scene.id,
      sceneName: scene.name,
      selectedContentId: selectedSceneContentId,
      mainContentId: scene.mainContentId,
      canMoveToNewVariant: selectedContentId !== chapter?.mainContentId && Boolean(ref && Object.entries(effectiveVariantSceneRefs).some(([variantId, refs]) => variantId !== selectedContentId && refs[scene.id]?.contentId === ref.contentId)),
      synchronized: ref
        ? sceneContentDocs[ref.contentId]?.scratchpadVersion === ref.version
        : chapter?.compiledSceneVersions?.[scene.id] === scene.updatedAt,
      variants: ids.map((id) => {
        const rawName = sceneContentDocs[id]?.name?.trim();
        const generatedDedicatedName = `${scene.name} - ${selectedContentDoc?.name || ''}`;
        const hasOwnName = Boolean(rawName && rawName !== scene.name && rawName !== generatedDedicatedName);
        const usedByVariantIds = Object.entries(effectiveVariantSceneRefs)
          .filter(([, refs]) => refs[scene.id]?.contentId === id)
          .map(([variantId]) => variantId);
        const isDedicatedToCurrent = id !== scene.mainContentId &&
          selectedContentId !== chapter?.mainContentId &&
          usedByVariantIds.length === 1 && usedByVariantIds[0] === selectedContentId;
        return {
          id,
          name: hasOwnName
            ? rawName!
            : id === scene.mainContentId
              ? translatedVariantLabel('default')
              : isDedicatedToCurrent
                ? translatedVariantLabel('dedicated')
                : translatedVariantLabel('unnamed'),
        };
      }),
    };
  }), [chapter?.compiledSceneVersions, chapter?.mainContentId, effectiveVariantSceneRefs, i18n.language, i18n.resolvedLanguage, sceneContentDocs, scenes, selectedContentDoc?.name, selectedContentId, selectedSceneRefs]);

  const handleUpdateVariantMetadata = async (name: string, notes: string) => {
    if (!novel.id || !selectedContentId) return;
    await backend.content.updateContent(novel.ownerId, novel, selectedContentId, { name, notes });
    setSelectedContentDoc((current) => current ? { ...current, name, notes } : current);
  };

  const handleMoveSceneToNewVariant = async (sceneId: string) => {
    if (!novel.id || !chapter || !selectedContentId || !selectedContentDoc || !getNewId) return;
    const scene = scenes.find((item) => item.id === sceneId);
    if (!scene) return;
    const sourceContentId = selectedSceneRefs?.[sceneId]?.contentId || scene.mainContentId;
    if (!sourceContentId) return;
    const isShared = Object.entries(effectiveVariantSceneRefs).some(([variantId, refs]) => variantId !== selectedContentId && refs[sceneId]?.contentId === sourceContentId);
    if (selectedContentId === chapter?.mainContentId || !isShared) return;
    const source = sceneContentDocs[sourceContentId] || await backend.content.getContent(novel.ownerId, novel, sourceContentId);
    if (!source) return;

    const id = getNewId();
    const dedicated = { ...source };
    delete (dedicated as Record<string, unknown>).comments;
    delete (dedicated as Record<string, unknown>).history;
    const now = new Date().toISOString();
    const newSceneVariant: ContentDocument = {
      ...dedicated,
      id,
      name: '',
      scratchpadVersion: 0,
      currentDayKey: now.split('T')[0],
      audit: { createdAt: now, updatedAt: now },
    };
    await backend.content.createContent(novel.ownerId, novel, newSceneVariant);

    const sceneVariantRefs = {
      ...(selectedSceneRefs || {}),
      [sceneId]: { contentId: id, version: 0 },
    };
    const contentVariantVersions = { ...(scene.contentVariantVersions || {}), [id]: 0 };
    await updateNarrativeItem('scenes', scene.id, {
      contentVariants: Array.from(new Set([...(scene.contentVariants || []), id])),
      contentVariantVersions,
    });
    const updatedChapter = {
      ...chapter,
      variantSceneRefs: { ...(chapter.variantSceneRefs || {}), [selectedContentId]: sceneVariantRefs },
    };
    await saveItem('narrative', 'chapters', updatedChapter);
    await updateNarrativeSummary('chapters', chapterId, updatedChapter);
    setSceneContentDocs((current) => ({ ...current, [id]: newSceneVariant }));
  };

  const handleCreateVariant = async (name: string, selections: Record<string, string>) => {
    if (!novel.id || !chapter || !selectedContentId || !getNewId) return;
    const source = await backend.content.getContent(novel.ownerId, novel, selectedContentId);
    if (!source) return;
    const id = getNewId();
    const sourceWithoutAnnotations = { ...source };
    delete (sourceWithoutAnnotations as Record<string, unknown>).comments;
    delete (sourceWithoutAnnotations as Record<string, unknown>).history;
    const sceneVariantRefs: Record<string, { contentId: string; version: number }> = {};
    const proseParts: Record<string, string> = {};
    for (const scene of scenes) {
      let contentId = selections[scene.id] || selectedSceneRefs?.[scene.id]?.contentId || scene.mainContentId;
      if (!contentId) continue;
      let sceneDoc = contentId === 'new' ? null : sceneContentDocs[contentId] || await backend.content.getContent(novel.ownerId, novel, contentId);
      if (contentId === 'new') {
        if (!scene.mainContentId) continue;
        const mainDoc = sceneContentDocs[scene.mainContentId] || await backend.content.getContent(novel.ownerId, novel, scene.mainContentId);
        if (!mainDoc) continue;
        contentId = getNewId();
        const dedicated = { ...mainDoc };
        delete (dedicated as Record<string, unknown>).comments;
        delete (dedicated as Record<string, unknown>).history;
        sceneDoc = {
          ...dedicated,
          id: contentId,
          name: '',
          scratchpadVersion: 0,
          currentDayKey: new Date().toISOString().split('T')[0],
          audit: { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        };
        await backend.content.createContent(novel.ownerId, novel, sceneDoc);
        await updateNarrativeItem('scenes', scene.id, {
          contentVariants: Array.from(new Set([...(scene.contentVariants || []), contentId])),
          contentVariantVersions: { ...(scene.contentVariantVersions || {}), [contentId]: 0 },
        });
      }
      if (!sceneDoc) continue;
      sceneVariantRefs[scene.id] = { contentId, version: sceneDoc.scratchpadVersion };
      proseParts[scene.id] = sceneDoc.scratchpad;
    }
    const sourceStructure = ensureStructuredChapterHtml(source.scratchpad || '', scenes.map((scene) => scene.id));
    const variantScratchpad = replaceSceneSections(sourceStructure, proseParts);
    const variant: ContentDocument = {
      ...sourceWithoutAnnotations,
      id,
      name,
      scratchpad: variantScratchpad,
      wordCount: calculateWordCount(variantScratchpad),
      scratchpadVersion: 0,
      currentDayKey: new Date().toISOString().split('T')[0],
      audit: { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    };
    await backend.content.createContent(novel.ownerId, novel, variant);
    const updatedChapter = {
      ...chapter,
      contentVariants: [...(chapter.contentVariants || []), id],
      variantSceneRefs: { ...(chapter.variantSceneRefs || {}), [id]: sceneVariantRefs },
    };
    await saveItem('narrative', 'chapters', updatedChapter);
    await updateNarrativeSummary('chapters', chapterId, updatedChapter);
    loadedContentId.current = id;
    setSelectedContentDoc(variant);
    setSelectedContentId(id);
    setIsDirty(false);
  };

  const handlePromoteVariant = async () => {
    if (!user?.uid || !novel.id || !chapter || !selectedContentId || selectedContentId === chapter.mainContentId || !getNewId) return;
    if (isDirty && !(await handleSave(editorContent))) return;

    const parts = sceneSectionMap(editorContent);
    if (!scenes.every((scene) => parts[scene.id] !== undefined)) {
      toast({
        title: t('common.errors.title'),
        description: t('chapters.publish.variant_scene_count_mismatch'),
        variant: 'destructive',
      });
      return;
    }

    const now = new Date().toISOString();
    const compiledSceneVersions: Record<string, string> = {};
    for (let index = 0; index < scenes.length; index++) {
      const scene = scenes[index];
      const associatedContentId = effectiveVariantSceneRefs[selectedContentId]?.[scene.id]?.contentId;
      if (associatedContentId) {
        const sceneVariants = Array.from(new Set([
          scene.mainContentId,
          ...(scene.contentVariants || []).filter((id) => id !== associatedContentId),
        ].filter((id): id is string => Boolean(id) && id !== associatedContentId)));
        await updateNarrativeItem('scenes', scene.id, {
          mainContentId: associatedContentId,
          contentVariants: sceneVariants,
        });
        compiledSceneVersions[scene.id] = now;
        continue;
      }
      // Legacy chapter variants have no exact association. Their save path
      // adopts the current scene main as a reference, so promotion remains safe
      // and never manufactures a new scene variant implicitly.
      if (scene.mainContentId) compiledSceneVersions[scene.id] = scene.updatedAt;
    }

    const variants = [chapter.mainContentId, ...(chapter.contentVariants || []).filter((id) => id !== selectedContentId)]
      .filter((id): id is string => Boolean(id));
    const updatedChapter = {
      ...chapter,
      mainContentId: selectedContentId,
      contentVariants: variants,
      compiledSceneVersions,
      updatedAt: now,
    };
    await saveItem('narrative', 'chapters', updatedChapter);
    await updateNarrativeSummary('chapters', chapterId, updatedChapter);
    toast({ title: t('chapters.publish.variant_promoted') });
  };

  const handleStatusChange = async (status: 'draft' | 'preview' | 'public') => {
    if (isDirty && !(await handleSave(editorContent))) return;
    await updatePublishStatus(chapterId, status);
  };

  const handleSyncAndReassemble = async () => {
    if (!novel.id || !chapter || !selectedContentId || isSaving) return;
    setIsSaving(true);
    try {
      const currentRefs = effectiveVariantSceneRefs[selectedContentId] || {};
      const repairedRefs: Record<string, { contentId: string; version: number }> = {};
      const replacementSections: Record<string, string> = {};
      for (const scene of scenes) {
        let contentId = currentRefs[scene.id]?.contentId;
        let contentDoc = contentId ? sceneContentDocs[contentId] || await backend.content.getContent(novel.ownerId, novel, contentId) : null;
        if (!contentDoc && scene.mainContentId) {
          contentId = scene.mainContentId;
          contentDoc = sceneContentDocs[contentId] || await backend.content.getContent(novel.ownerId, novel, contentId);
        }
        if (!contentId || !contentDoc) continue;
        const version = contentDoc.scratchpadVersion ?? 0;
        repairedRefs[scene.id] = { contentId, version };
        replacementSections[scene.id] = stripCommentAnchors(contentDoc.scratchpad || '');
        if (scene.contentVariantVersions?.[contentId] !== version) {
          await updateNarrativeItem('scenes', scene.id, {
            contentVariantVersions: { ...(scene.contentVariantVersions || {}), [contentId]: version }
          });
        }
      }
      const structuredCurrent = ensureStructuredChapterHtml(editorContent, scenes.map((scene) => scene.id));
      const reassembledContent = replaceSceneSections(structuredCurrent, replacementSections);
      const reassembledWordCount = calculateWordCount(reassembledContent);
      await backend.content.updateContent(novel.ownerId, novel, selectedContentId, { scratchpad: reassembledContent, wordCount: reassembledWordCount });
      const updatedChapter = { ...chapter, variantSceneRefs: { ...(chapter.variantSceneRefs || {}), [selectedContentId]: repairedRefs } };
      await saveItem('narrative', 'chapters', updatedChapter);
      await updateNarrativeSummary('chapters', chapterId, updatedChapter);
      setEditorContent(reassembledContent);
      setSelectedContentDoc((current) => current ? { ...current, scratchpad: reassembledContent, wordCount: reassembledWordCount } : current);
      setIsDirty(false);
      toast({ title: t('chapters.publish.assembled_title'), description: t('chapters.publish.variant_reassembled') });
    } catch (error) {
      console.error('Failed to synchronize chapter variant:', error);
      toast({ title: t('common.errors.title'), description: t('chapters.publish.assembled_failed'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const assembleFinalEdition = async (targetChapterId: string, force = false) => {
    if (mainContentId) {
      await handleSyncAndReassemble();
      return selectedContentId;
    }
    return assembleChapterFinalEdition(targetChapterId, force);
  };

  const keepFinalEdition = async () => {
    if (!chapter || !novel.id) return;
    const versions = Object.fromEntries(
      (Object.values(novel.summaries?.scenes || {}) as NarrativeSummary[])
        .filter((scene) => scene.chapterId === chapterId && !scene.isArchive)
        .map((scene) => [scene.id, scene.updatedAt])
    );
    const updatedChapter = { ...chapter, compiledSceneVersions: versions };
    await saveItem('narrative', 'chapters', updatedChapter);
    await updateNarrativeSummary('chapters', chapterId, updatedChapter);
  };

  if (isLoading || isAssembling) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2, bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          {isAssembling ? t('chapters.publish.assembling') : t('editor.loading_text')}
        </Typography>
      </Box>
    );
  }

  if (!chapter) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', height: '100vh' }}>
        <Typography variant="h5" color="error">{t('chapters.publish.not_found')}</Typography>
        <Button component={Link} to="/scenes" startIcon={<ArrowBack />} sx={{ mt: 2 }} variant="contained">
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <OutlineDrawer
        isOpen={isOutlineOpen}
        onClose={() => setIsOutlineOpen(false)}
        expandedChapters={expandedChapters}
        setExpandedChapters={setExpandedChapters}
        chapters={outlineChapters}
        scenes={outlineScenes}
        onSceneClick={(scene) => {
          if (scene.mainContentId) {
            void navigate({ to: '/editor', search: { textId: scene.mainContentId } });
          }
        }}
        onCreateScene={async (name, targetChapterId) => {
          await createFullScene(name, targetChapterId);
        }}
        onCreateChapter={createChapter}
        onReorderScenes={handleReorderScenes}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
      <PublishEditorHeader
        chapterName={chapter.name}
        publishStatus={(chapter.publishStatus as 'draft' | 'preview' | 'public') || 'draft'}
        onStatusChange={handleStatusChange}
        wordCount={wordCount}
        readingTime={readingTime}
        isSaving={isSaving}
        isChangingStatus={isUpdatingStatus}
        isDirty={isDirty}
        onSave={() => { void handleSave(); }}
        canUseAutosave={canUseAutosave}
        autosaveEnabled={autosaveEnabled}
        onAutosaveToggle={setAutosaveEnabled}
        mainContentId={selectedContentId || undefined}
        canPublish={user?.uid === novel?.ownerId || !!userData?.isAdmin}
        publicationId={novel?.publicationId}
        chapterPublicId={chapter.chapterPublicId}
        variantsOpen={variantsOpen}
        onToggleVariants={() => setVariantsOpen((open) => !open)}
        isOutlineOpen={isOutlineOpen}
        onToggleOutline={() => setIsOutlineOpen((open) => !open)}
      />

      {/* Editor Content Area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {hasDrift && (
          <Box sx={{ p: 2, pb: 0 }}>
            <Alert
              severity="warning"
              action={
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={keepFinalEdition}>{t('chapters.publish.keep_final')}</Button>
                  <Button color="inherit" size="small" startIcon={<Sync />} onClick={() => assembleFinalEdition(chapterId)}>
                    {t('chapters.publish.sync_action')}
                  </Button>
                </Stack>
              }
              sx={{ borderRadius: 3 }}
            >
              <AlertTitle>{t('chapters.publish.drift_title')}</AlertTitle>
              {t('chapters.publish.drift_desc')}
            </Alert>
          </Box>
        )}

        {!mainContentId ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, p: 4, textAlign: 'center' }}>
            <MenuBook sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              {t('chapters.publish.not_assembled_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              {t('chapters.publish.not_assembled_desc')}
            </Typography>
            <Button variant="contained" color="primary" onClick={() => assembleFinalEdition(chapterId)} sx={{ borderRadius: 2, px: 4, py: 1 }}>
              {t('chapters.publish.assemble_action')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <RichTextEditor
              sectionControls
              sectionLabels={Object.fromEntries(scenes.map((scene, index) => [scene.id, `${index + 1} · ${scene.name}`]))}
              content={editorContent}
              onChange={handleEditorChange}
              onCommentChange={handleCommentChange}
              onSave={async (content) => {
                if (content !== undefined) setEditorContent(content);
                await handleSave(content);
              }}
              textId={selectedContentId}
            />
          </Box>
        )}
      </Box>
      <UnreadyScenesDialog
        isOpen={!!unreadyData}
        scenes={unreadyData?.scenes || []}
        onClose={() => setUnreadyData(null)}
        onConfirm={async () => {
          if (unreadyData) {
            await assembleFinalEdition(unreadyData.chapterId, true);
            setUnreadyData(null);
          }
        }}
        isAssembling={isAssembling}
      />
      <ChapterSaveProgressDialog progress={saveProgress} />
      <ChapterVariantsDrawer
        open={variantsOpen}
        variants={variantOptions.map((variant) => variant.id === selectedContentId && selectedContentDoc ? { ...variant, name: selectedContentDoc.name, notes: selectedContentDoc.notes } : variant)}
        selectedId={selectedContentId || mainContentId || ''}
        disabled={isSaving}
        sceneVariants={chapterSceneVariants}
        onClose={() => setVariantsOpen(false)}
        onSelect={setSelectedContentId}
        onCreate={handleCreateVariant}
        onMoveSceneToNewVariant={handleMoveSceneToNewVariant}
        onPromote={handlePromoteVariant}
        promotingPublicChapter={chapter.publishStatus === 'public'}
        onUpdateMetadata={handleUpdateVariantMetadata}
      />
      </Box>
    </Box>
  );
}
